import React from 'react';
import PipelineBoard from '../views/PipelineBoard';
import { getValueRangeLabel } from '../forms/OppModal';
import { formatDate } from '../../utils/dateHelpers';
import useStageChange from '../../hooks/useStageChange';
import { useGamification } from '../gamification/GamificationContext';

/**
 * OpportunityPipeline
 *
 * Shared wrapper around PipelineBoard that includes all stage-change modals
 * (Actual Value, Follow-Up, Open Tasks Warning). Used by both DashboardPage
 * and OppPage so the pipeline behaviour is identical everywhere.
 *
 * Props:
 *   opportunities      {Array}    Enriched opportunity objects (must include assignedUserName)
 *   onOpportunityClick {Function} Called when a pipeline card is clicked
 *   user               {Object}   Authenticated user (for useStageChange)
 *   setToast           {Function} Toast callback ({ message, type })
 */
const OpportunityPipeline = ({ opportunities, onOpportunityClick, user, setToast }) => {
  const { recordActivity } = useGamification();
  const {
    handleStageChange,
    actualValueModal, setActualValueModal,
    pendingActualValue, setPendingActualValue,
    handleActualValueSubmit,
    followUpModal, pendingFollowUp, setPendingFollowUp, followUpError,
    handleFollowUpSubmit, handleFollowUpSkip, handleFollowUpCancel,
    showTaskWarning, pendingStageChange, openTasksData,
    handleConfirmStageChange, handleCancelStageChange,
  } = useStageChange({ user, setToast, recordActivity });

  return (
    <>
      <PipelineBoard
        opportunities={opportunities}
        onOpportunityClick={onOpportunityClick}
        onStageChange={handleStageChange}
      />

      {/* Actual Value Capture Modal for Closed Won */}
      {actualValueModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              🎉 Congratulations on the Win!
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Please enter the actual value of this closed deal for accurate revenue reporting.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Actual Value <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 pl-7 rounded"
                  value={pendingActualValue}
                  onChange={e => setPendingActualValue(e.target.value)}
                  placeholder="Enter actual deal value"
                  min="0"
                  step="0.01"
                  autoFocus
                />
              </div>
              {actualValueModal.opp && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Estimated range: {getValueRangeLabel(actualValueModal.opp.value)}
                </p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setActualValueModal({ show: false, opp: null })}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleActualValueSubmit}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
              >
                Save & Close Won
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-Up Modal for Not Qualified / Closed Lost */}
      {followUpModal.show && followUpModal.opp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {followUpModal.newStage === 'Not Qualified' ? 'Opportunity Not Qualified' : 'Opportunity Closed Lost'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Please provide a reason for moving <strong>{followUpModal.opp.customerName || followUpModal.opp.opportunityNumberLabel || 'this opportunity'}</strong> to {followUpModal.newStage}.
            </p>

            {/* Required reason field — always visible */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason for Stage Change <span className="text-red-600">*</span>
              </label>
              <textarea
                value={pendingFollowUp.reason}
                onChange={(e) => setPendingFollowUp(prev => ({ ...prev, reason: e.target.value }))}
                rows={2}
                maxLength={1000}
                placeholder="Why is this opportunity being moved to this stage?"
                className={`w-full px-3 py-2 border ${followUpError ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none`}
                autoFocus
              />
              {followUpError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{followUpError}</p>
              )}
            </div>

            {/* Optional re-engagement flag */}
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Would you also like to flag this opportunity for future re-engagement?
            </p>

            <label className="flex items-center gap-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={pendingFollowUp.flag}
                onChange={(e) => setPendingFollowUp(prev => ({ ...prev, flag: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Flag for re-engagement follow-up</span>
            </label>

            {pendingFollowUp.flag && (
              <div className="space-y-3 pl-7 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Follow-Up Date</label>
                  <input
                    type="date"
                    value={pendingFollowUp.date}
                    onChange={(e) => setPendingFollowUp(prev => ({ ...prev, date: e.target.value }))}
                    className="w-48 px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                  <textarea
                    value={pendingFollowUp.notes}
                    onChange={(e) => setPendingFollowUp(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    maxLength={1000}
                    placeholder="Reason for re-engagement, key details…"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleFollowUpCancel}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={pendingFollowUp.flag ? handleFollowUpSubmit : handleFollowUpSkip}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded"
              >
                {pendingFollowUp.flag ? 'Flag & Change Stage' : 'Change Stage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Open Tasks Warning Modal */}
      {showTaskWarning && pendingStageChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-amber-500 px-4 py-3">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Open Tasks Warning
              </h3>
            </div>
            <div className="p-4">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                This opportunity has <strong className="text-amber-600">{openTasksData.count} open task{openTasksData.count !== 1 ? 's' : ''}</strong> for the <strong>"{pendingStageChange.opp.stage}"</strong> stage that should be completed before advancing to <strong>"{pendingStageChange.newStage}"</strong>.
              </p>
              {openTasksData.openTasks.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded p-3 mb-3 max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Open Tasks:</p>
                  <ul className="space-y-1">
                    {openTasksData.openTasks.map(task => (
                      <li key={task.id} className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
                        <span className="text-amber-500 mr-2">•</span>
                        <span>
                          <strong>{task.taskLabel}</strong>: {task.title || task.subject || 'Untitled task'}
                          {task.dueDate && (
                            <span className="text-xs text-gray-500 ml-2">
                              (Due: {formatDate(task.dueDate)})
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to advance the stage anyway?
              </p>
            </div>
            <div className="flex justify-end space-x-2 px-4 py-3 bg-gray-50 dark:bg-gray-700">
              <button
                type="button"
                onClick={handleCancelStageChange}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Stay on "{pendingStageChange.opp.stage}"
              </button>
              <button
                type="button"
                onClick={handleConfirmStageChange}
                className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 transition-colors"
              >
                Advance Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OpportunityPipeline;
